import _ from "lodash";
/**-- external -- */
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { Tooltip, useKeyboardShortcutsContext } from "@toddle-edu/ds-web";
import React, { useRef, useState, useEffect } from "react";
import { PinFilled, PinOutlined } from "@toddle-edu/ds-icons";

/**-- internal -- */
import { useI18n } from "Hooks";
import MoreItemWithToolTip from "UIComponents/MoreItemWithToolTip";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { updateBookmarkCourse } from "Teacher/modules/TeacherModule";
import { ShortcutChip } from "GlobalHeader/components/JumpToFeature/commonComponents";
import { recordEvent } from "lib/eventTracker";

/**-- relative -- */
import CourseIcon from "../CourseIcon";
import classes from "./CourseDetails.scss";

import { colors } from "Constants";
import { getIsGlobalSearchJumpToRevampEnabled } from "GlobalHeader/modules/Utils";
import {
  updateCourseBookmark,
  updateClassBookmark,
} from "LearningCoursesList/modules/LearningCoursesListModule";

const customStyles = {
  grades: {
    color: colors.textSubtle,
    fontSize: "1.2rem",
    lineHeight: "1.6rem",
  },
};
const courseDetailsTooltipOverlayStyle = { pointerEvents: "none" };

const TextWithTooltip = ({ title, className }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const titleRef = useRef();

  const checkAndSetTruncation = () => {
    const node = titleRef.current;

    if (node) {
      setShowTooltip(node.scrollWidth > node.clientWidth);
    }
  };

  useEffect(() => {
    checkAndSetTruncation();
    window.addEventListener("resize", checkAndSetTruncation);
    return () => {
      window.removeEventListener("resize", checkAndSetTruncation);
    };
  }, [title]);

  return (
    <ConditionalWrapper
      type="wrapperAsElement"
      condition={showTooltip}
      wrapper={
        <Tooltip
          tooltip={title}
          placement="bottom"
          overlayStyle={courseDetailsTooltipOverlayStyle}
        />
      }
    >
      <div className={className} ref={titleRef}>
        {title}
      </div>
    </ConditionalWrapper>
  );
};

const CourseDetails = props => {
  const {
    testId,
    course,
    courseOrder,
    onCourseClick,
    updateBookmarkCourse,
    updateCourseBookmark,
    updateClassBookmark,
    isLearningCoursesEnabled,
    isLearningCoursesFeed,
    columnIndex,
  } = props;

  const courseId = _.get(course, "id", "");
  const title = _.get(course, "title", "");
  const grades = _.get(course, "grades", []);
  const isBookmark = isLearningCoursesFeed
    ? _.get(course, "isBookmarked", false)
    : _.get(course, "isBookmark", false);
  const genericTags = isLearningCoursesFeed
    ? {}
    : _.get(course, "genericTags", []);

  const gradeNameList = _.map(grades, grade => grade.name);

  const genericTagsList = _.join(
    _.map(genericTags, tag => tag.label),
    ", "
  );

  const gradeNameListWithGenericTags = _.map(gradeNameList, gradeName =>
    _.isEmpty(genericTagsList) ? gradeName : `${gradeName} • ${genericTagsList}`
  );

  const handlePinClick = e => {
    e.stopPropagation();

    recordEvent({
      eventName: "JUMP_TO",
      eventData: {
        event_type: "PIN_COURSE",
        courseId: _.get(course, "id"),
        action: isBookmark ? "UNPIN" : "PIN",
      },
    });

    if (isLearningCoursesEnabled) {
      const payload = { courseId, isBookmarked: isBookmark };

      isLearningCoursesFeed
        ? updateCourseBookmark(payload)
        : updateClassBookmark({ classId: courseId, isBookmark });
    } else {
      const courseInput = isBookmark
        ? { removeCourseIds: [courseId] }
        : { addCourseIds: [courseId] };

      updateBookmarkCourse(courseInput);
    }
  };

  const pinIconContainerClass = classNames(
    { [classes.pinIconContainer]: true },
    {
      [classes.pinIconContainerBookmarked]: isBookmark,
    },
    {
      [classes.pinnedState]: isBookmark,
    }
  );

  const { t } = useI18n(["common"]);

  const isGlobalSearchJumpToRevampEnabled = getIsGlobalSearchJumpToRevampEnabled();

  const { areShortcutsEnabled } = useKeyboardShortcutsContext();

  const containerClasses = isGlobalSearchJumpToRevampEnabled
    ? classes.containerV2
    : classes.container;

  const leftContainerClasses = isGlobalSearchJumpToRevampEnabled
    ? classes.leftContainerV2
    : classes.leftContainer;

  const courseDetailsContainerClasses = isGlobalSearchJumpToRevampEnabled
    ? classes.courseDetailsContainerV2
    : classes.courseDetailsContainer;

  const moreItemWithToolTipStyles = isGlobalSearchJumpToRevampEnabled
    ? customStyles.grades
    : {};

  const pinIconSize = isGlobalSearchJumpToRevampEnabled
    ? "xxx-small"
    : "xx-small";

  const learningCourseTitleClasses = classNames(
    classes.learningCourseTitle,
    "text-secondary truncate"
  );

  const courseNameClasses = classNames("text-body-2", {
    [classes.courseNameV2]: isGlobalSearchJumpToRevampEnabled,
    [classes.courseName]: !isGlobalSearchJumpToRevampEnabled,
  });

  return (
    <div
      className={containerClasses}
      onClick={onCourseClick}
      data-elm={`jump-to-col-${columnIndex}`}
      tabIndex="-1"
      data-test-id={`${testId}-courseItem-${courseId}`}
    >
      <div className={leftContainerClasses}>
        <CourseIcon
          course={course}
          isLearningCoursesEnabled={isLearningCoursesEnabled}
          isLearningCoursesFeed={isLearningCoursesFeed}
        />
        <div className={courseDetailsContainerClasses}>
          <TextWithTooltip title={title} className={courseNameClasses} />
          {isLearningCoursesEnabled && !isLearningCoursesFeed ? (
            <TextWithTooltip
              title={_.get(course, "learningCourse[0].title", "-")}
              className={learningCourseTitleClasses}
            />
          ) : (
            <MoreItemWithToolTip
              items={gradeNameListWithGenericTags}
              length={1}
              noItemText={t("common:no_with_label", {
                label: t("common:grade_plural"),
              })}
              stopPropagationOnLabelClick={false}
              trigger={["hover"]}
              containerStyle={moreItemWithToolTipStyles}
            />
          )}
        </div>
      </div>
      <div className={classes.rightContainer}>
        <div
          className={pinIconContainerClass}
          onClick={handlePinClick}
          data-test-id={`${testId}-bookmarkCourse-${courseId}`}
        >
          {isBookmark ? (
            <PinFilled size={pinIconSize} />
          ) : (
            <PinOutlined variant="subtle" size={pinIconSize} />
          )}
        </div>
        {courseOrder !== null && areShortcutsEnabled ? (
          <ShortcutChip
            shortcutKey={courseOrder}
            isLearningCoursesFeed={isLearningCoursesFeed}
          />
        ) : null}
      </div>
    </div>
  );
};

export default compose(
  connect(null, {
    updateBookmarkCourse,
    updateCourseBookmark,
    updateClassBookmark,
  })
)(CourseDetails);
